import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import TwilioController from '../controllers/TwilioController';
import commonHandler from '../errorHandlers/common-handler';
import {
  TwilioAreaCodes,
  TwilioNumber,
  TwilioRecentDiscarded,
  TwilioTestNumber,
} from '../interfaces/twilio';
import { StoreState } from '../store-interfaces';
import connectedPageCRUDActions from './ConnectedPageCRUDActions';
import CrudActionContainer from './CrudActionContainer';

class TwilioCRUDActions extends CrudActionContainer {
  getAllNumbersAction: string;
  setCountryAreaCodesAction: string;
  setAvailableNumberAction: string;
  clearAvailableNumbersAction: string;
  resetTwilioAction: string;
  updateAction: string;
  updateActionSuccess: string;

  constructor() {
    super('TWILIO');

    this.getAllNumbersAction = this.makeActionKey('GET_ALL_NUMBERS');
    this.setAvailableNumberAction = this.makeActionKey('SET_AVAILABLE_NUMBERS');
    this.setCountryAreaCodesAction = this.makeActionKey(
      'SET_COUNTRY_AREA_CODES',
    );
    this.clearAvailableNumbersAction = this.makeActionKey(
      'CLEAR_AVAILABLE_NUMBERS',
    );
    this.clearAvailableNumbersAction = this.makeActionKey(
      'CLEAR_AVAILABLE_NUMBERS',
    );
    this.resetTwilioAction = this.makeActionKey('RESET_TWILIO');
    this.updateAction = this.makeActionKey('UPDATE');
    this.updateActionSuccess = this.makeActionKey('UPDATE_SUCCESS');
  }

  getAll(facebookPageId: number, twilio: boolean) {
    return async (dispatch: Dispatch<StoreState>) => {
      if (!twilio) {
        return;
      }
      try {
        const res = (await TwilioController.getAll(facebookPageId)) || {};

        const {
          numbers = [],
          testNumbers = [],
          recentDiscarded,
          callForwardingNumber = '',
        } = res;

        dispatch(
          this.getAllNumbersActionCreator(
            recentDiscarded ? (res as TwilioRecentDiscarded) : numbers,
            testNumbers,
            callForwardingNumber,
          ),
        );
        return numbers;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  activatePage(
    facebookPageId: number,
    restoreNumber: boolean,
    {
      numberKind,
      number,
      skipFetchActivePage,
    }: {
      numberKind?: string;
      number?: string;
      skipFetchActivePage?: boolean;
    },
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const {
          numbers = [],
          testNumbers = [],
          callForwardingNumber = '',
        } = (await TwilioController.activatePage(
          facebookPageId,
          restoreNumber,
          {
            numberKind,
            number,
          },
        )) || {};

        dispatch(
          this.getAllNumbersActionCreator(
            numbers,
            testNumbers,
            callForwardingNumber,
          ),
        );
        if (!skipFetchActivePage) {
          dispatch(connectedPageCRUDActions.retrievePage(facebookPageId));
        }

        return numbers;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  getAllNumbersActionCreator = (
    numbers: TwilioNumber[] | TwilioRecentDiscarded,
    testNumbers: TwilioTestNumber[],
    callForwardingNumber: string,
  ) => ({
    type: this.getAllNumbersAction,
    numbers,
    testNumbers,
    callForwardingNumber,
  });

  getCountryCodesActionCreator = (
    us: TwilioAreaCodes,
    canada: TwilioAreaCodes,
  ) => ({
    type: this.setCountryAreaCodesAction,
    codes: [us, canada],
  });

  clearAvailableNumbersActionCreator = () => ({
    type: this.clearAvailableNumbersAction,
  });

  resetTwilioActionCreator = () => ({
    type: this.resetTwilioAction,
  });

  setAvailableNumberActionCreator = (numbers: TwilioNumber[]) => ({
    type: this.setAvailableNumberAction,
    numbers,
  });

  updateSuccessActionCreator = ({
    testNumbers,
    callForwardingNumber,
  }: {
    testNumbers: TwilioTestNumber[];
    callForwardingNumber: string;
  }) => ({
    type: this.updateActionSuccess,
    testNumbers,
    callForwardingNumber,
  });

  getCountryCodes(countries: string[]) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const [us, canada] = await TwilioController.getCountryCodes(countries);
        dispatch(this.getCountryCodesActionCreator(us, canada));
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  getAvailableNumbers(
    facebookPageId: number,
    countryCode: string,
    areaCode: string,
    pageSize?: number,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy());
        const numbers = await TwilioController.getAvailableNumbers(
          facebookPageId,
          countryCode,
          areaCode,
          pageSize,
        );
        dispatch(this.setAvailableNumberActionCreator(numbers));
        dispatch(this.unbusy());
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  clearNumbers() {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.clearAvailableNumbersActionCreator());
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  deletePhonenumber(facebookPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy());
        await TwilioController.deletePhonenumber(facebookPageId);
        dispatch(connectedPageCRUDActions.retrievePage(facebookPageId));
        dispatch(this.resetTwilioActionCreator());
        dispatch(this.unbusy());
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  update(...args: Parameters<typeof TwilioController.update>) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy());
        const res = await TwilioController.update(...args);
        dispatch(this.updateSuccessActionCreator(res));
        dispatch(this.unbusy());
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }
}

const twilioCRUDActions = new TwilioCRUDActions();
export default twilioCRUDActions;

export const useTwilioCRUDActions = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  return useMemo(
    () => ({
      getCountryCodes: (countries: string[]) =>
        dispatch(twilioCRUDActions.getCountryCodes(countries)),
      getAvailableNumbers: (
        facebookPageId: number,
        countryCode: string,
        areaCode: string,
        pageSize?: number,
      ) =>
        dispatch(
          twilioCRUDActions.getAvailableNumbers(
            facebookPageId,
            countryCode,
            areaCode,
            pageSize,
          ),
        ),
      getAll: (facebookPageId: number, twillioFlag: boolean) =>
        dispatch(twilioCRUDActions.getAll(facebookPageId, twillioFlag)),
      activatePage: (
        ...args: Parameters<typeof twilioCRUDActions.activatePage>
      ) => dispatch(twilioCRUDActions.activatePage(...args)),
      clearNumbers: () => dispatch(twilioCRUDActions.clearNumbers()),
      updateTestNumbers: (
        ...args: Parameters<typeof twilioCRUDActions.update>
      ) => dispatch(twilioCRUDActions.update(...args)),
      updateCallsForwarding: (
        ...args: Parameters<typeof twilioCRUDActions.update>
      ) => dispatch(twilioCRUDActions.update(...args)),
    }),
    [dispatch],
  );
};
